import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Observable, debounceTime, filter, map, of, switchMap, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { GbeEndPointV4 } from '@core/models/gbe/gbe-endpoint.enum';
import { CurrencyService } from '@core/services/currency/currency.service';
import * as AuthActions from '@core/store/auth/auth.actions';
import { GBE_BASE_URL } from '@core/tokens/gbe-url.token';
import { extractGbeErrorMessage } from '@core/utils/extract-gbe-error';

import {
  clearLegacyPreferences,
  collectLegacyPreferences,
  isEmptyPreferences
} from './legacy-preferences.util';
import {
  USER_PREFERENCES_MUTATION_ACTIONS,
  UserPreferencesActions
} from './user-preferences.actions';
import { UserPreferences } from './user-preferences.model';
import {
  selectUserPreferences,
  selectUserPreferencesLoaded
} from './user-preferences.selectors';

interface PreferencesResponse {
  preferences: UserPreferences;
}

export const PERSIST_DEBOUNCE_MS = 500;

@Injectable()
export class UserPreferencesEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly http = inject(HttpClient);
  private readonly url = `${inject(GBE_BASE_URL)}/${GbeEndPointV4.USER_PREFERENCES}`;

  /**
   * Loads the blob from GBE. Network retries (and the generic error toast on
   * 4xx) are handled by `gbeErrorInterceptor`, so this effect only maps the
   * outcome: success → `loadSuccess`, any surfaced error → `loadFailure`.
   */
  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPreferencesActions.load),
      switchMap(() =>
        this.http.get<PreferencesResponse>(this.url).pipe(
          map((res) =>
            UserPreferencesActions.loadSuccess({ preferences: res?.preferences ?? {} })
          ),
          catchError((error) =>
            of(UserPreferencesActions.loadFailure({ error: this.message(error) }))
          )
        )
      )
    )
  );

  /**
   * One-time migration. Only when the backend is empty do we collect any legacy
   * localStorage preferences and hand them to `migrateLegacy` (which persists
   * them and clears localStorage only on a confirmed `PUT`). We never delete
   * legacy keys here: if the backend already owns preferences we didn't migrate
   * anything, so the user's local keys are left untouched rather than dropped.
   */
  migrate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPreferencesActions.loadSuccess),
      switchMap(({ preferences }) => {
        if (!isEmptyPreferences(preferences)) {
          return [];
        }
        const migrated = collectLegacyPreferences();
        if (isEmptyPreferences(migrated)) {
          return [];
        }
        return [UserPreferencesActions.migrateLegacy({ migrated })];
      })
    )
  );

  /**
   * Persists migrated legacy preferences. Crucially, localStorage is cleared
   * ONLY after the `PUT` is confirmed successful — if it fails (network blip,
   * backend not yet deployed, oversized payload rejected) the legacy keys are
   * left intact so the user's customisations are not destroyed.
   */
  persistMigration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPreferencesActions.migrateLegacy),
      switchMap(({ migrated }) =>
        this.put$(migrated).pipe(
          map(() => {
            clearLegacyPreferences();
            return UserPreferencesActions.persistSuccess();
          }),
          catchError((error) =>
            of(UserPreferencesActions.persistFailure({ error: this.message(error) }))
          )
        )
      )
    )
  );

  /**
   * Debounced whole-document persistence on any mutation, gated on a successful
   * initial load so a failed `GET` can never lead to overwriting the backend
   * with an empty blob.
   *
   * Note: each tab holds its own in-memory copy and PUTs the full document, so
   * concurrent edits across tabs are last-writer-wins. This is an accepted
   * trade-off for preferences (a regression from the previously shared
   * localStorage), kept deliberately simple.
   */
  persist$ = createEffect(() =>
    this.actions$.pipe(
      ofType(...USER_PREFERENCES_MUTATION_ACTIONS),
      debounceTime(PERSIST_DEBOUNCE_MS),
      concatLatestFrom(() => [
        this.store.select(selectUserPreferences),
        this.store.select(selectUserPreferencesLoaded)
      ]),
      filter(([, , loaded]) => loaded),
      switchMap(([, preferences]) =>
        this.put$(preferences).pipe(
          map(() => UserPreferencesActions.persistSuccess()),
          catchError((error) =>
            of(UserPreferencesActions.persistFailure({ error: this.message(error) }))
          )
        )
      )
    )
  );

  /**
   * Keeps the synchronous currency cache (read by non-DI consumers such as
   * `kpi.model`) in sync with the store — the single place this happens now.
   */
  syncCurrencyCache$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          UserPreferencesActions.loadSuccess,
          UserPreferencesActions.migrateLegacy,
          UserPreferencesActions.setCurrency
        ),
        concatLatestFrom(() => this.store.select(selectUserPreferences)),
        tap(([, preferences]) => {
          if (preferences.currency) {
            CurrencyService.setCurrentCurrency(preferences.currency);
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * Clears the synchronous currency cache on logout so a next user without a
   * currency preference doesn't inherit the previous user's currency (the store
   * slice is already reset by the reducer on logout).
   */
  resetCurrencyCacheOnLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logout),
        tap(() => CurrencyService.resetCurrentCurrency())
      ),
    { dispatch: false }
  );

  /**
   * Logs persistence failures for diagnostics. The user-facing notification and
   * the 4xx-vs-network distinction (retry network, toast on 4xx) are handled by
   * `gbeErrorInterceptor`; here we keep `persistError` in the store as
   * representable state instead of swallowing the error silently.
   */
  logPersistError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserPreferencesActions.persistFailure),
        tap(({ error }) => console.error('Failed to persist user preferences:', error))
      ),
    { dispatch: false }
  );

  private put$(preferences: UserPreferences): Observable<unknown> {
    return this.http.put<PreferencesResponse>(this.url, { preferences });
  }

  private message(error: unknown): string {
    return extractGbeErrorMessage(error, 'Failed to reach the user preferences endpoint');
  }
}
